import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listAuditRunsAdmin,
  setAuditRunVisibility,
  deleteAuditRun,
  setAuditRunExclusions,
  type AdminAuditRunRow,
} from "@/lib/audit.functions";

export const Route = createFileRoute("/admin/audits")({
  component: AdminAuditsPage,
});

function AdminAuditsPage() {
  const qc = useQueryClient();
  const fetchList = useServerFn(listAuditRunsAdmin);
  const setVis = useServerFn(setAuditRunVisibility);
  const del = useServerFn(deleteAuditRun);
  const setExcl = useServerFn(setAuditRunExclusions);

  const query = useQuery({
    queryKey: ["admin-audits"],
    queryFn: () => fetchList(),
    staleTime: 10_000,
  });

  const [filter, setFilter] = useState("");
  const [pending, setPending] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminAuditRunRow | null>(null);

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["admin-audits"] });
    qc.invalidateQueries({ queryKey: ["leaderboard"] });
    qc.invalidateQueries({ queryKey: ["publicAudit"] });
    qc.invalidateQueries({ queryKey: ["embedAudit"] });
  };

  const visMut = useMutation({
    mutationFn: (v: { id: string; isPublic: boolean }) => setVis({ data: v }),
    onMutate: (v) => setPending(v.id),
    onSettled: () => setPending(null),
    onSuccess: invalidateAll,
  });

  const delMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onMutate: (id) => setPending(id),
    onSettled: () => setPending(null),
    onSuccess: invalidateAll,
  });

  const exclMut = useMutation({
    mutationFn: (v: { id: string; excludedIds: string[] }) => setExcl({ data: v }),
    onMutate: (v) => setPending(v.id),
    onSettled: () => setPending(null),
    onSuccess: () => {
      invalidateAll();
      setEditing(null);
    },
  });

  const runs = useMemo(() => query.data?.runs ?? [], [query.data?.runs]);
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return runs;
    return runs.filter(
      (r) =>
        r.hostname.toLowerCase().includes(q) ||
        r.slug.toLowerCase().includes(q) ||
        r.input_url.toLowerCase().includes(q),
    );
  }, [runs, filter]);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold text-foreground">
            Auditorías guardadas
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Oculta o elimina resultados públicos. Ocultar es reversible; eliminar no lo es.
          </p>
        </div>
        <input
          type="search"
          placeholder="Buscar hostname, slug o URL…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-72 rounded-lg border border-border bg-background px-3 py-2 text-sm"
        />
      </header>

      {query.isPending && <p className="text-sm text-muted-foreground">Cargando…</p>}
      {query.isError && (
        <p className="text-sm text-red-500">Error: {(query.error as Error).message}</p>
      )}

      {!query.isPending && filtered.length === 0 && (
        <p className="rounded-2xl border border-border bg-card/20 p-8 text-center text-sm text-muted-foreground">
          No hay auditorías que coincidan.
        </p>
      )}

      <div className="overflow-x-auto rounded-2xl border border-border">
        <table className="min-w-full text-sm">
          <thead className="bg-card/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Store</th>
              <th className="px-4 py-3">Locale</th>
              <th className="px-4 py-3">AEO</th>
              <th className="px-4 py-3">Store</th>
              <th className="px-4 py-3">Fecha</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map((r) => (
              <Row
                key={r.id}
                run={r}
                busy={pending === r.id}
                onToggle={() => visMut.mutate({ id: r.id, isPublic: !r.is_public })}
                onExclude={() => setEditing(r)}
                onDelete={() => {
                  if (
                    confirm(
                      `¿Eliminar auditoría de ${r.hostname} (${r.locale.toUpperCase()})? Esta acción no se puede deshacer.`,
                    )
                  ) {
                    delMut.mutate(r.id);
                  }
                }}
              />
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <ExclusionsDialog
          run={editing}
          busy={pending === editing.id}
          onClose={() => setEditing(null)}
          onSave={(excludedIds) => exclMut.mutate({ id: editing.id, excludedIds })}
        />
      )}
    </div>
  );
}

function Row({
  run,
  busy,
  onToggle,
  onExclude,
  onDelete,
}: {
  run: AdminAuditRunRow;
  busy: boolean;
  onToggle: () => void;
  onExclude: () => void;
  onDelete: () => void;
}) {
  const publicPath =
    run.locale === "es" ? `/es/auditoria/resultados/${run.slug}` : `/audit/results/${run.slug}`;
  return (
    <tr className={busy ? "opacity-50" : ""}>
      <td className="px-4 py-3">
        <div className="font-medium text-foreground">{run.hostname}</div>
        <div className="text-xs text-muted-foreground">{run.slug}</div>
      </td>
      <td className="px-4 py-3">
        <span className="rounded-full border border-border bg-background/40 px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
          {run.locale}
        </span>
      </td>
      <td className="px-4 py-3">{run.aeo_score ?? ", "}</td>
      <td className="px-4 py-3">{run.store_score ?? ", "}</td>
      <td className="px-4 py-3 text-xs text-muted-foreground">
        {new Date(run.created_at).toLocaleString()}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-col gap-1">
          <span
            className={
              "inline-flex w-fit rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider " +
              (run.is_public
                ? "bg-emerald-500/15 text-emerald-500"
                : "bg-muted text-muted-foreground")
            }
          >
            {run.is_public ? "Público" : "Oculto"}
          </span>
          {run.is_pointer && (
            <span className="inline-flex w-fit rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-500">
              ★ Pointer
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex justify-end gap-2">
          <a
            href={publicPath}
            target="_blank"
            rel="noreferrer"
            className="rounded-full border border-border px-3 py-1 text-xs text-foreground hover:bg-card"
          >
            Ver
          </a>
          <button
            onClick={onToggle}
            disabled={busy}
            className="rounded-full border border-border px-3 py-1 text-xs text-foreground hover:bg-card disabled:opacity-50"
          >
            {run.is_public ? "Ocultar" : "Publicar"}
          </button>
          <button
            onClick={onExclude}
            disabled={busy}
            className="rounded-full border border-border px-3 py-1 text-xs text-foreground hover:bg-card disabled:opacity-50"
          >
            Excluir checks{run.excluded_ids.length > 0 ? ` (${run.excluded_ids.length})` : ""}
          </button>
          <button
            onClick={onDelete}
            disabled={busy}
            className="rounded-full border border-red-500/40 px-3 py-1 text-xs text-red-500 hover:bg-red-500/10 disabled:opacity-50"
          >
            Eliminar
          </button>
        </div>
      </td>
    </tr>
  );
}

function ExclusionsDialog({
  run,
  busy,
  onClose,
  onSave,
}: {
  run: AdminAuditRunRow;
  busy: boolean;
  onClose: () => void;
  onSave: (excludedIds: string[]) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(() => new Set(run.excluded_ids));
  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Preview recomputed scores (client-side mirror of server math)
  const storeChecks = run.available_checks.store;
  const aeoChecks = run.available_checks.aeo;

  const previewStore = (() => {
    const kept = storeChecks.filter((c) => !selected.has(c.id));
    if (kept.length === 0) return null;
    const points: Record<string, number> = { pass: 100, warning: 60, fail: 0 };
    const total = kept.reduce((s, c) => s + c.weight, 0);
    if (total === 0) return null;
    const weighted = kept.reduce((s, c) => s + (points[c.status] ?? 0) * c.weight, 0);
    return Math.round(weighted / total);
  })();
  const previewAeo = (() => {
    const kept = aeoChecks.filter((c) => !selected.has(c.id));
    if (kept.length === 0) return null;
    return Math.round(kept.reduce((s, c) => s + c.score, 0) / kept.length);
  })();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-border bg-background p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold text-foreground">
              Excluir checks del score
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {run.hostname} · {run.locale.toUpperCase()}. Los checks excluidos no cuentan para el
              promedio y aparecen marcados en la página pública.
            </p>
          </div>
          <button onClick={onClose} className="rounded-full border border-border px-3 py-1 text-xs">
            Cerrar
          </button>
        </div>

        <div className="mb-4 grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg border border-border bg-card/40 p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Store (preview)
            </div>
            <div className="font-display text-2xl">
              {previewStore ?? ", "}{" "}
              <span className="text-xs text-muted-foreground">
                (actual {run.store_score ?? ", "})
              </span>
            </div>
          </div>
          <div className="rounded-lg border border-border bg-card/40 p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              AEO (preview)
            </div>
            <div className="font-display text-2xl">
              {previewAeo ?? ", "}{" "}
              <span className="text-xs text-muted-foreground">(actual {run.aeo_score ?? ", "})</span>
            </div>
          </div>
        </div>

        <CheckGroup
          title="Store checks"
          items={storeChecks.map((c) => ({
            id: c.id,
            label: c.label,
            status: c.status,
            meta: `peso ${c.weight}`,
          }))}
          selected={selected}
          onToggle={toggle}
        />
        <CheckGroup
          title="AEO checks"
          items={aeoChecks.map((c) => ({
            id: c.id,
            label: c.label,
            status: c.status,
            meta: `score ${c.score}`,
          }))}
          selected={selected}
          onToggle={toggle}
        />

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="rounded-full border border-border px-4 py-2 text-sm disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={() => onSave(Array.from(selected))}
            disabled={busy}
            className="rounded-full bg-foreground px-4 py-2 text-sm font-semibold text-background disabled:opacity-50"
          >
            {busy ? "Guardando…" : "Guardar y recalcular"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CheckGroup({
  title,
  items,
  selected,
  onToggle,
}: {
  title: string;
  items: { id: string; label: string; status: string; meta: string }[];
  selected: Set<string>;
  onToggle: (id: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div className="mt-4">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </div>
      <ul className="divide-y divide-border rounded-lg border border-border">
        {items.map((c) => (
          <li key={c.id} className="flex items-center gap-3 p-3">
            <input
              type="checkbox"
              checked={selected.has(c.id)}
              onChange={() => onToggle(c.id)}
              className="h-4 w-4"
            />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-foreground">{c.label}</div>
              <div className="text-[11px] text-muted-foreground">
                {c.id} · {c.status} · {c.meta}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
